import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Alert } from '../../components/ui/Alert';
import { Button } from '../../components/ui/Button';
import { ApiError } from '../../lib/api';
import { Modal } from '../albums/Modal';
import { useSession } from '../../stores/session';
import { copy } from '../../config/copy';
import { rooms } from './api';
import { useLiveHosts } from './useLiveHosts';

/**
 * Sending somebody else's track to a room that is on air.
 *
 * ## Why this exists at all
 *
 * The whole earned-boost economy is built on one action - you find a track,
 * you put it in front of a host, the host plays it through - and until now
 * there was no way to perform it. Every layer supported it: the API never
 * checked ownership, the queue stores the submitter separately from the
 * artist, and the credit ledger pays out only when those two differ. The one
 * missing piece was a screen, because the submit dialog offers your own
 * catalogue and nothing else. So the reward could be read about and not
 * earned.
 *
 * ## Here rather than only in the room
 *
 * The moment somebody decides a track deserves an audience is the moment they
 * are listening to it, which is this page. Requiring them to remember it until
 * they next open a live room is how a good call becomes a forgotten one.
 *
 * ## Always offered, not only while somebody is on air
 *
 * Hiding it when no room is live would keep the feature exactly as invisible
 * as it has been. It opens either way and says who is on, because "nobody is
 * broadcasting right now" is an answer and a missing button is not.
 */
export function SendToRoom({
  trackId,
  ownedByMe,
}: {
  trackId: number;
  /** Only the reward line depends on this - the action itself is the same. */
  ownedByMe: boolean;
}) {
  const me = useSession((state) => state.user);
  const queryClient = useQueryClient();
  const { items, isPending } = useLiveHosts();

  const [open, setOpen] = useState(false);
  const [sentTo, setSentTo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const send = useMutation({
    mutationFn: (username: string) => rooms.submit(username, trackId, 'free', null),
    onSuccess: (_result, username) => {
      setSentTo(username);
      setError(null);
      setOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['room', username] });
      void queryClient.invalidateQueries({ queryKey: ['me', 'queue'] });
    },
    onError: (caught: unknown) => {
      /*
       * The server's own sentence. Every refusal here is something the person
       * can act on - a room that has closed submissions, a track already in
       * that queue - and "that did not work" sends them back to try the same
       * thing again.
       */
      setError(caught instanceof ApiError ? caught.message : 'That could not be sent.');
    },
  });

  // Signed out there is nothing to submit with, and the server would refuse.
  if (!me) return null;

  return (
    <>
      <Button
        variant="ghost"
        data-send-to-room={trackId}
        onClick={() => {
          setError(null);
          setOpen(true);
        }}
      >
        {sentTo ? 'Sent' : 'Send to a live room'}
      </Button>

      {open && (
        <Modal title="Send to a live room" onClose={() => setOpen(false)}>
          <div className="flex flex-col gap-4 p-5" data-send-to-room-dialog>
            {error && <Alert tone="error">{error}</Alert>}

            {/*
              Said before the choice, not after it. Somebody deciding whether
              this is worth doing is deciding it now.
            */}
            {!ownedByMe && (
              <p className="text-sm leading-relaxed text-soft">
                If the host plays it through, this counts toward a{' '}
                <span className="text-ink">{copy['noun.boost.earned']}</span>. Your own tracks
                never count — this is for music you think somebody should hear.
              </p>
            )}

            {isPending ? (
              <p className="font-mono text-[11px] text-soft">Looking…</p>
            ) : items.length === 0 ? (
              <p className="text-sm text-soft">
                Nobody is on air right now. A track can only be sent to a room while its host is
                broadcasting.
              </p>
            ) : (
              <ul className="flex max-h-80 flex-col gap-1.5 overflow-y-auto">
                {items.map((room) => (
                  <li key={room.id}>
                    <button
                      type="button"
                      data-send-to={room.username}
                      disabled={send.isPending}
                      onClick={() => send.mutate(room.username)}
                      className="flex w-full items-center gap-3 rounded-[var(--radius-control)] border border-line p-3 text-left transition-colors hover:border-line-strong hover:bg-raised disabled:opacity-60"
                    >
                      <span className="flex min-w-0 flex-1 flex-col">
                        <span className="truncate text-sm font-medium text-ink">
                          {room.name ?? room.host.display_name}
                        </span>
                        <span className="truncate font-mono text-[11px] text-soft">
                          @{room.username}
                        </span>
                      </span>
                      <span className="meta-caps shrink-0 text-faint">
                        {room.listener_count} listening
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
